/* eslint-disable unicorn/no-object-as-default-parameter */
/* eslint-disable @typescript-eslint/no-unused-expressions */
import axios from "axios";
import {
  getPostDataFromTypedPost,
  Post,
  PostApiInput,
  normalizePlatformType,
} from "features/post";
import { normalizePostMediaShape } from "features/post/utils";
import { extractApiError } from "utils/get-error-message";
import type { PostCreationSource } from "utils/post-creation-source";
import { skedApi } from "./_skedApi";
import { GeneratedSkedApiClient } from "./generated-client";
import type { paths } from "./generated/sked-api-openapi-types";

const apiHashtagUrl = "https://hashtag.skedsocial.com/recommend";
const jsonUnsafeCharacterMap = {
  "<": String.raw`\u003C`,
  ">": String.raw`\u003E`,
  "&": String.raw`\u0026`,
} as const;

type GetPostByIdResponse =
  paths["/sked/posts/{postId}"]["get"]["responses"][200]["content"]["application/json"];
type GetPostByIdClientResponse = GetPostByIdResponse & {
  accountId?: string;
  accountIds?: string[];
};

type DraftApiInput = {
  _id: string;
  accountIds: string[];
  schedule: PostApiInput;
};

function getPostIdentifier(post: { _id?: string; id?: string }): string {
  const postId = post._id || post.id;
  if (!postId) {
    throw new Error("Post identifier is required");
  }
  return postId;
}

export function stringifyJsonBodyForWaf(data: unknown): string {
  const json = JSON.stringify(data);
  if (json === undefined) {
    throw new Error("Failed to serialize request body for WAF-safe transport");
  }

  return json.replaceAll(
    /[<>&]/g,
    (character) =>
      jsonUnsafeCharacterMap[character as keyof typeof jsonUnsafeCharacterMap]
  );
}

// WAF blocks JSON request bodies containing raw <, >, and & characters (SKE-3198).
// Unicode-escaping preserves the payload value after JSON.parse on the server.
// Ampersands are valid caption text, so they are escaped only in transport.
function createWafSafeJsonConfig() {
  return {
    headers: {
      "Content-Type": "application/json",
    },
    transformRequest: [(data: unknown) => stringifyJsonBodyForWaf(data)],
  };
}

export async function getPostById(postId: string) {
  const generatedSkedApiClient = new GeneratedSkedApiClient<paths>();
  const result = await generatedSkedApiClient.client.GET(
    "/sked/posts/{postId}",
    {
      params: {
        path: { postId },
      },
    }
  );
  const data = (await generatedSkedApiClient.unwrap(
    result
  )) as GetPostByIdClientResponse;

  return {
    ...data,
    accountIds: data.accountIds || [data.accountId],
  };
}

export type GenerateAltTextPayload =
  | {
      assetId: string;
      mediaUrl?: never;
      /** Optional SEO/topic hint that biases generation (server-side prompt). */
      keywords?: string;
    }
  | {
      assetId?: never;
      mediaUrl: string;
      keywords?: string;
    };

export async function generateAltText(
  payload: GenerateAltTextPayload,
  options?: { signal?: AbortSignal }
): Promise<{ altText: string }> {
  const { data } = await skedApi.patch<{ altText: string }>(
    "/content-inbox/alt-text/generate",
    payload,
    { signal: options?.signal }
  );
  return data;
}

export async function createDraft(
  post: Post,
  creationSource?: PostCreationSource
) {
  const data = parseDataForCreatingPost(post, "draft", creationSource);
  const rs = await axios.post(`/schedules`, data, createWafSafeJsonConfig());
  return rs.data as string[];
}

export async function createScheduledPost(
  post: Post,
  creationSource?: PostCreationSource
) {
  const data = parseDataForCreatingPost(post, "schedule", creationSource);
  const rs = await axios.post(`/schedules`, data, createWafSafeJsonConfig());
  return rs.data as string[];
}

export async function createQueuedPost(
  post: Post,
  creationSource?: PostCreationSource
) {
  const data = parseDataForCreatingPost(post, "queue", creationSource);
  const rs = await axios.post(`/schedules`, data, createWafSafeJsonConfig());
  return rs.data as string[];
}

export type SchedulePayload = PostApiInput & { actionType: string };

export function schedulesValidate(data: {
  accountIds: string[];
  schedules: SchedulePayload[];
  timezone: string;
  uploader: string;
}): Promise<unknown> {
  return axios
    .post(`/schedules/validate`, data, createWafSafeJsonConfig())
    .catch((error) => {
      throw extractApiError(error);
    });
}
export async function editDraftValidation(post: Post): Promise<void> {
  const data = postToDraftApiInput(post);
  const postId = getPostIdentifier(post);
  await axios.put(
    `/drafts/${postId}/validate`,
    data,
    createWafSafeJsonConfig()
  );
}

export async function editDraft(
  post: Post,
  { validate }: { validate: boolean } = { validate: true }
): Promise<void> {
  const data = postToDraftApiInput(post);
  const postId = getPostIdentifier(post);
  validate && (await editDraftValidation(post));
  await axios.put(`/drafts/${postId}`, data, createWafSafeJsonConfig());
}

export async function moveDraftToSchedule(post: Post): Promise<string[][]> {
  try {
    const data = postToDraftApiInput(post);
    const postId = getPostIdentifier(post);
    const rs = await axios.put(
      `/drafts/${postId}/schedule`,
      data,
      createWafSafeJsonConfig()
    );
    return rs.data;
  } catch (error) {
    throw extractApiError(error);
  }
}

export async function moveQueueToDraft(postId: string) {
  try {
    const { data } = await axios.post(`/queue/${postId}/draft`);
    return data.newId;
  } catch (error) {
    throw extractApiError(error);
  }
}

export async function moveScheduleToDraft(postId: string): Promise<string> {
  try {
    const { data } = await axios.post(`/schedules/${postId}/draft`);
    return data.newId;
  } catch (error) {
    throw extractApiError(error);
  }
}

export async function moveQueueToSchedule(
  post: Post
): Promise<{ data: string[] }> {
  const data = postToScheduledPostApiInput(post);
  const postId = getPostIdentifier(post);
  return await axios.put(
    `/queue/${postId}/schedule`,
    data,
    createWafSafeJsonConfig()
  );
}

/** handled split */
export async function editScheduledPost(post: Post): Promise<string[]> {
  const data = postToScheduledPostApiInput(post);
  const postId = getPostIdentifier(post);
  const { data: ids } = await axios.put(
    `/schedules/${postId}`,
    data,
    createWafSafeJsonConfig()
  );
  return ids;
}

/** handled split */
export async function editQueuedPost(post: Post): Promise<string[]> {
  const data = postToScheduledPostApiInput(post);
  const postId = getPostIdentifier(post);
  const rs = await axios.put(
    `/queue/${postId}`,
    data,
    createWafSafeJsonConfig()
  );
  return rs.data;
}

export async function moveDraftToQueue(
  postId: string | string[],
  creationSource?: PostCreationSource
): Promise<string[]> {
  const rs = await axios.post(`/drafts/bulk/queue`, {
    postIds: Array.isArray(postId) ? postId : [postId],
    ...(creationSource ? { creationSource } : {}),
  });
  return rs.data;
}

export async function moveScheduledPostToQueue(
  postId: string | string[],
  creationSource?: PostCreationSource,
  postStatusKey?: string | null
) {
  const { data } = await axios.post(`/posts/bulk/queue`, {
    postIds: Array.isArray(postId) ? postId : [postId],
    ...(creationSource ? { creationSource } : {}),
    ...(postStatusKey !== undefined ? { postStatusKey } : {}),
  });

  return data;
}

function parseDataForCreatingPost(
  post: Post,
  state: string,
  creationSource?: PostCreationSource
) {
  const normalizedPost = normalizePostMediaShape(post);
  return {
    ...(creationSource ? { creationSource } : {}),
    accountIds: normalizedPost.accountIds,
    schedules: [
      {
        actionType: state,
        ...getPostDataFromTypedPost(normalizedPost),
      },
    ],
  };
}

function postToDraftApiInput(post: Post): DraftApiInput {
  const normalizedPost = normalizePostMediaShape(post);
  const postId = getPostIdentifier(normalizedPost);
  return <DraftApiInput>{
    _id: postId,
    // how this works previously??
    accountIds: normalizedPost.accountIds,
    schedule: getPostDataFromTypedPost(normalizedPost),
  };
}

function postToScheduledPostApiInput(post: Post) {
  const normalizedPost = normalizePostMediaShape(post);
  const postId = getPostIdentifier(normalizedPost);
  normalizedPost.platformType = normalizePlatformType(
    String(normalizedPost.platformType)
  );

  return {
    ...getPostDataFromTypedPost(normalizedPost),
    _id: postId,
    accountIds: normalizedPost.accountIds,
  };
}

export interface HashtagRecommendation {
  hashtag: string;
  metrics: {
    engagement: number;
    popularity: number;
    relevancy: Record<string, number>;
  };
}

export async function getRecommendedTags(
  data: {
    hashtags: string[];
  },
  signal?: AbortSignal
) {
  const response = await axios.request<{
    recommendation?: HashtagRecommendation[];
  }>({
    method: "post",
    url: apiHashtagUrl,
    data,
    signal,
  });
  return response.data.recommendation ?? [];
}

export async function markTagNegative(
  data: { hashtags: string[]; irrelevantHashtag: string },
  signal?: AbortSignal
) {
  const response = await axios.request<{
    recommendation?: HashtagRecommendation[];
  }>({
    method: "post",
    url: `${apiHashtagUrl}/negative`,
    data,
    signal,
  });
  return response.data.recommendation ?? [];
}

export async function markTagPositive(
  data: { hashtags: string[]; relevantHashtag: string },
  signal?: AbortSignal
): Promise<void> {
  await axios.request({
    method: "post",
    url: `${apiHashtagUrl}/positive`,
    data,
    signal,
  });
}

interface checkOverlappingPostsArguments {
  accountIds: string[];
  date: string;
  postId: string | undefined;
}

export async function checkOverlappingPosts({
  accountIds,
  date,
  postId,
}: checkOverlappingPostsArguments): Promise<unknown> {
  const { data } = await axios.get("/instagram/checktime", {
    params: { accountIds, date, postId },
  });

  return data;
}
